// frontend/src/features/campaign/components/create/Step2TargetCohort.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from '../../../../api/axios';
import { getStageClass, getScoreClass, normalizeLead } from '../../utils/campaignUtils';

export default function Step2TargetCohort({
  formData,
  isReadOnly = false,
  isGeneratingAi = false,
  onRegenerateAudience,
  onOpenImportModal,
  onEnrichAndAddLushaLeads,
  importedSearch,
  setImportedSearch,
  selectedImportedIds,
  onToggleSelectImported,
  onToggleSelectAllImported,
  onDeleteSelectedImported,
  onRemoveImportedLead,
}) {
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [hasQueriedLusha, setHasQueriedLusha] = useState(false);
  const [suggestedContacts, setSuggestedContacts] = useState([]);
  const [lushaTotal, setLushaTotal] = useState(null); // real totalResults from Lusha API
  const [selectedSuggestedIds, setSelectedSuggestedIds] = useState(new Set());
  const [enrichType, setEnrichType] = useState('both'); // 'both' | 'email' | 'phone'
  const [isEnriching, setIsEnriching] = useState(false);
  const [suggestionFeedback, setSuggestionFeedback] = useState('');
  const [activeFunnelStage, setActiveFunnelStage] = useState(null);
  const [candidatePage, setCandidatePage] = useState(1);
  const [candidateSearch, setCandidateSearch] = useState('');
  const candidatePageSize = 10;

  const targetRegion = formData?.targetRegion || 'Malaysia & Singapore';
  const industrySector = formData?.industrySector || 'Technology & SaaS';
  const targetPersonas = useMemo(() => {
    return Array.isArray(formData?.targetPersonas) && formData.targetPersonas.length > 0
      ? formData.targetPersonas
      : [
          'Vice President of Sales (VP Sales)',
          'Chief Revenue Officer (CRO)',
          'Head of Business Development',
        ];
  }, [formData?.targetPersonas]);

  // ── Existing & Enriched Cohort Leads in Table (both Lusha & Lead Pipeline) ──
  const normalizedCohortLeads = useMemo(() => {
    return (formData.importedLeads || []).map((lead, idx) => normalizeLead(lead, idx));
  }, [formData.importedLeads]);

  // Count of selected leads currently in the Campaign Cohort Table
  const totalCohortLeadsCount = normalizedCohortLeads.length;
  const candidateSelectedCount = selectedSuggestedIds.size;

  // Blue bar count represents active selected leads in the table (or selected candidates if table empty)
  const finalSelectedLeadsCount =
    totalCohortLeadsCount > 0 ? totalCohortLeadsCount : candidateSelectedCount;

  // ── Dynamic Funnel Metrics (Derived from AI blueprint — progressively narrowing) ──
  const universe = Number(formData?.audienceUniverse) || 5200;
  const geoMatch = Number(formData?.geoMatchCount) || Math.round(universe * 0.65);
  const indMatch = Number(formData?.industryMatchCount) || Math.round(universe * 0.37);
  // AI blueprint estimate — must stay ≤ indMatch to keep funnel valid
  const perMatch = Number(formData?.personaMatchCount) || 720;
  // Lusha real total (used only in section header, not funnel — Lusha counts globally, not funnel-scoped)
  const lushaDisplayTotal = lushaTotal !== null ? lushaTotal : suggestedContacts.length;

  const geoWidth = `${Math.min(100, Math.max(22, Math.round((geoMatch / universe) * 100)))}%`;
  const indWidth = `${Math.min(100, Math.max(18, Math.round((indMatch / universe) * 100)))}%`;
  const perWidth = `${Math.min(100, Math.max(14, Math.round((perMatch / universe) * 100)))}%`;
  const finalWidth =
    finalSelectedLeadsCount > 0
      ? `${Math.min(100, Math.max(16, Math.round((finalSelectedLeadsCount / (perMatch || 1)) * 100)))}%`
      : '16%';

  // ── Total matched candidates (same value used in both funnel step 4 and Lusha section header) ──
  const totalAvailableCandidatesCount = perMatch || 720;

  // ── Clean & Normalize Target Personas and Filters for Lusha Query ──
  const cleanJobTitles = useMemo(() => {
    return targetPersonas
      .map((p) => {
        if (!p || typeof p !== 'string') return '';
        // Strip parenthetical abbreviations like "(CTO)", "(VP Sales)" to match Lusha directory standards
        return p.replace(/\s*\([^)]*\)/g, '').trim();
      })
      .filter(Boolean);
  }, [targetPersonas]);

  // ── On-Demand Fetch Lusha Leads when User clicks Generate/Discover ──
  const fetchLushaSuggestions = useCallback(async () => {
    setHasQueriedLusha(true);
    setLoadingSuggestions(true);
    setSuggestionFeedback('');
    try {
      // Split compound regions like "Malaysia & Singapore" into individual country objects
      // Lusha requires each location to be an object like { country: "Malaysia" }
      const regionLocations = targetRegion
        .split(/\s*[&\/]\s*|\s+and\s+/i)
        .map((r) => r.trim())
        .filter(Boolean)
        .map((country) => ({ country }));

      const contactInclude = {
        jobTitles: cleanJobTitles.length > 0 ? cleanJobTitles : targetPersonas,
        locations: regionLocations.length > 0 ? regionLocations : [{ country: 'Malaysia' }],
      };

      const payload = {
        filters: {
          contacts: {
            include: contactInclude,
          },
        },
        page: 0,
        size: 50,
        includePartialContact: true,
      };

      const res = await axios.post('/lusha/search/contacts', payload);

      // Backend spreads result.data flat: { contacts: [...], totalResults, _creditsInfo }
      // Lusha API itself returns { contacts: [...], pagination: {...} }
      console.log('[Lusha] raw res.data keys:', Object.keys(res?.data || {}));
      const flat = res?.data || {};
      // totalResults = real total matched from Lusha pagination (not just page size)
      const apiTotal =
        flat.totalResults || flat.pagination?.totalResults || flat.pagination?.total || null;
      const raw = flat.contacts || flat.data || (Array.isArray(flat) ? flat : []);

      if (Array.isArray(raw) && raw.length > 0) {
        const mapped = raw.map((c, idx) => {
          const cid = String(c.id || c.contactId || c.personId || `sug_${idx + 1}`);
          const fullName =
            c.fullName ||
            c.name ||
            `${c.firstName || ''} ${c.lastName || ''}`.trim() ||
            `Candidate Lead ${idx + 1}`;
          const title =
            c.jobTitle || c.title || targetPersonas[idx % targetPersonas.length] || 'Executive';
          const company =
            c.companyName ||
            c.company?.name ||
            c.organization?.name ||
            c.company ||
            'Enterprise Solutions';
          const location = c.location || c.country || targetRegion;

          return {
            id: cid,
            contactId: cid,
            name: fullName,
            fullName,
            jobTitle: title,
            company,
            companyName: company,
            location,
            country: location,
            industry: industrySector,
            leadScore: 92 - (idx % 3) * 8,
            hasEmail: c.hasEmail !== false,
            hasPhone: c.hasPhone !== false,
            isEmailRevealed: false,
            isPhoneRevealed: false,
            linkedinUrl: c.linkedinUrl || c.socialLinks?.linkedin || null,
            raw: c,
          };
        });

        setSuggestedContacts(mapped);
        // Use real API total; fall back to page count if not provided
        setLushaTotal(apiTotal !== null ? apiTotal : raw.length);
        return;
      }
      // If Lusha returns no matched contacts or success is false
      setSuggestedContacts([]);
      setLushaTotal(0);
    } catch (err) {
      console.warn('Lusha search query note:', err?.message || err);
      // Strict: No manual fallback; display empty matching lead profile
      setSuggestedContacts([]);
      setLushaTotal(null);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [cleanJobTitles, industrySector, targetPersonas, targetRegion]);

  // ── Selected candidate credit calculation ──
  // NOTE: defined before priority tiers so the tiers can use it as the active pool
  const selectedCandidatesList = useMemo(() => {
    return suggestedContacts.filter((c) => selectedSuggestedIds.has(c.id));
  }, [suggestedContacts, selectedSuggestedIds]);

  // ── Priority Tier Breakdown ──
  // Mirror exactly what bar 5 counts:
  //   - If cohort leads exist → use those (bar 5 = cohort count)
  //   - Else if Lusha candidates are selected → use selected (bar 5 = candidateSelectedCount)
  //   - Else → use full suggested pool (nothing selected yet)
  const activeLeadPool =
    totalCohortLeadsCount > 0
      ? normalizedCohortLeads
      : candidateSelectedCount > 0
        ? selectedCandidatesList
        : suggestedContacts;
  const highPriorityCount = activeLeadPool.filter((l) => (l.leadScore ?? 85) >= 80).length;
  const medPriorityCount = activeLeadPool.filter(
    (l) => (l.leadScore ?? 70) >= 60 && (l.leadScore ?? 70) < 80
  ).length;
  const lowPriorityCount = activeLeadPool.filter((l) => (l.leadScore ?? 50) < 60).length;

  // ── Filtered & Paginated Candidates ──
  const filteredCandidates = useMemo(() => {
    if (!candidateSearch.trim()) return suggestedContacts;
    const q = candidateSearch.toLowerCase();
    return suggestedContacts.filter(
      (c) =>
        c.name?.toLowerCase().includes(q) ||
        c.company?.toLowerCase().includes(q) ||
        c.jobTitle?.toLowerCase().includes(q) ||
        c.location?.toLowerCase().includes(q)
    );
  }, [suggestedContacts, candidateSearch]);

  const totalCandidatePages = Math.max(1, Math.ceil(filteredCandidates.length / candidatePageSize));

  const paginatedCandidates = useMemo(() => {
    const startIdx = (candidatePage - 1) * candidatePageSize;
    return filteredCandidates.slice(startIdx, startIdx + candidatePageSize);
  }, [filteredCandidates, candidatePage, candidatePageSize]);

  const totalCreditsNeeded = useMemo(() => {
    return selectedCandidatesList.reduce((sum, c) => {
      let cost = 0;
      if (enrichType === 'both') {
        if (c.hasEmail) cost += 1;
        if (c.hasPhone) cost += 5;
      } else if (enrichType === 'email') {
        if (c.hasEmail) cost += 1;
      } else if (enrichType === 'phone') {
        if (c.hasPhone) cost += 5;
      }
      return sum + cost;
    }, 0);
  }, [selectedCandidatesList, enrichType]);

  const handleToggleSelectCandidate = (id) => {
    setSelectedSuggestedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSelectCurrentPage = () => {
    const pageIds = paginatedCandidates.map((c) => c.id);
    const allPageSelected =
      pageIds.length > 0 && pageIds.every((id) => selectedSuggestedIds.has(id));

    setSelectedSuggestedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleSelectAllMatchedPool = () => {
    if (selectedSuggestedIds.size === suggestedContacts.length) {
      setSelectedSuggestedIds(new Set());
    } else {
      setSelectedSuggestedIds(new Set(suggestedContacts.map((c) => c.id)));
    }
  };

  const handleEnrichSelectedCandidates = async () => {
    if (selectedCandidatesList.length === 0 || !onEnrichAndAddLushaLeads) return;
    setIsEnriching(true);
    setSuggestionFeedback('');

    try {
      await onEnrichAndAddLushaLeads({
        selectedContacts: selectedCandidatesList,
        enrichType,
        totalCredits: totalCreditsNeeded,
      });

      // Remove enriched leads from candidate suggestions
      const enrichedIds = new Set(selectedCandidatesList.map((c) => c.id));
      setSuggestedContacts((prev) => prev.filter((c) => !enrichedIds.has(c.id)));
      setSelectedSuggestedIds(new Set());
      setSuggestionFeedback(
        `✓ Successfully enriched ${selectedCandidatesList.length} lead(s), added to Campaign Cohort and saved to CRM Lead Pipeline (Stage: New)!`
      );
    } catch (err) {
      console.error('Enrichment failed:', err);
      setSuggestionFeedback('Failed to enrich selected leads. Please try again.');
    } finally {
      setIsEnriching(false);
    }
  };

  const filteredCohortLeads = normalizedCohortLeads.filter((lead) => {
    if (!importedSearch.trim()) return true;
    const q = importedSearch.toLowerCase();
    return (
      lead.name?.toLowerCase().includes(q) ||
      lead.company?.toLowerCase().includes(q) ||
      lead.jobTitle?.toLowerCase().includes(q) ||
      lead.industry?.toLowerCase().includes(q) ||
      lead.country?.toLowerCase().includes(q) ||
      lead.email?.toLowerCase().includes(q) ||
      lead.source?.toLowerCase().includes(q)
    );
  });

  const isAllImportedSelected =
    filteredCohortLeads.length > 0 &&
    filteredCohortLeads.every((l) => selectedImportedIds.has(l.id));

  const isInteractionDisabled = isReadOnly || isGeneratingAi || isEnriching;

  return (
    <div className={`cmp-blueprint-step-pane ${isInteractionDisabled ? 'read-only' : ''}`}>
      <div className="cmp-blueprint-tag-pill">
        STEP 2 OF 6 • AUDIENCE SELECTION & ELIGIBILITY FUNNEL
      </div>

      <div className="cmp-step-intro-row">
        <div>
          <h2 className="cmp-blueprint-heading">Target Audience & Qualification Funnel</h2>
          <p className="cmp-blueprint-subheading">
            Live funnel analysis based on Lusha prospect discovery and your selected campaign cohort
            leads.
          </p>
        </div>
        <div className="cmp-step-actions-group">
          <button
            type="button"
            className="cmp-btn-secondary"
            disabled={isInteractionDisabled}
            onClick={() => {
              if (onRegenerateAudience) onRegenerateAudience();
              fetchLushaSuggestions();
            }}
          >
            <i className={`fas fa-sync-alt ${loadingSuggestions ? 'fa-spin' : ''}`} /> Re-Analyze
            Audience Cohort
          </button>
          <button
            type="button"
            className="cmp-btn-primary cmp-btn-import-pipeline-trigger"
            disabled={isInteractionDisabled}
            onClick={onOpenImportModal}
          >
            <i className="fas fa-file-import" /> Import from Lead Pipeline
          </button>
        </div>
      </div>

      {/* ── INTERACTIVE QUALIFICATION FUNNEL BAR (POWERED BY LUSHA & SELECTED COHORT) ── */}
      <div className="cmp-funnel-card">
        <div
          className={`cmp-funnel-step ${activeFunnelStage === 1 ? 'active-funnel-step' : ''}`}
          onClick={() => setActiveFunnelStage(activeFunnelStage === 1 ? null : 1)}
          title="Total available prospect universe pool in market database"
        >
          <div className="cmp-funnel-bar" style={{ width: '100%' }}>
            <span>1. Total Prospect Universe Pool</span>
            <span className="cmp-funnel-bar-count">{universe.toLocaleString()} prospects</span>
          </div>
        </div>

        <div
          className={`cmp-funnel-step ${activeFunnelStage === 2 ? 'active-funnel-step' : ''}`}
          onClick={() => setActiveFunnelStage(activeFunnelStage === 2 ? null : 2)}
          title={`Lusha prospects matching target region: ${targetRegion}`}
        >
          <div className="cmp-funnel-bar" style={{ width: geoWidth }}>
            <span>2. Matches Target Region ({targetRegion})</span>
            <span className="cmp-funnel-bar-count">{geoMatch.toLocaleString()}</span>
          </div>
        </div>

        <div
          className={`cmp-funnel-step ${activeFunnelStage === 3 ? 'active-funnel-step' : ''}`}
          onClick={() => setActiveFunnelStage(activeFunnelStage === 3 ? null : 3)}
          title={`Lusha prospects matching sector: ${industrySector}`}
        >
          <div className="cmp-funnel-bar" style={{ width: indWidth }}>
            <span>3. Matches Sector ({industrySector})</span>
            <span className="cmp-funnel-bar-count">{indMatch.toLocaleString()}</span>
          </div>
        </div>

        <div
          className={`cmp-funnel-step ${activeFunnelStage === 4 ? 'active-funnel-step' : ''}`}
          onClick={() => setActiveFunnelStage(activeFunnelStage === 4 ? null : 4)}
          title={`Lusha prospects matching target personas (${targetPersonas.length} Roles)`}
        >
          <div className="cmp-funnel-bar" style={{ width: perWidth }}>
            <span>4. Matches Personas ({targetPersonas.length} Roles)</span>
            <span className="cmp-funnel-bar-count">{perMatch.toLocaleString()}</span>
          </div>
        </div>

        {/* ── BLUE COLOR BAR: SELECTED LEADS IN TABLE (LUSHA + LEAD PIPELINE) ── */}
        <div
          className={`cmp-funnel-step highlight ${activeFunnelStage === 5 ? 'active-funnel-step' : ''}`}
          onClick={() => setActiveFunnelStage(activeFunnelStage === 5 ? null : 5)}
          title="Selected campaign cohort leads (both Lusha enriched & CRM Pipeline)"
        >
          <div className="cmp-funnel-bar final" style={{ width: finalWidth }}>
            <span>5. Final Selected Campaign Cohort Leads (Lusha & Pipeline)</span>
            <span className="cmp-funnel-bar-count">
              {finalSelectedLeadsCount > 0
                ? `${finalSelectedLeadsCount} selected lead${finalSelectedLeadsCount !== 1 ? 's' : ''}`
                : '0 leads selected'}
            </span>
          </div>
        </div>
      </div>

      {/* ── PRIORITY TIER CARDS (CALCULATED FROM ACTIVE LEADS) ── */}
      <div className="cmp-audience-breakdown-grid">
        <div className="cmp-audience-stat-box high">
          <span className="cmp-aud-label">
            <i className="fas fa-star" /> High Priority Tier
          </span>
          <span className="cmp-aud-value">
            {highPriorityCount} lead{highPriorityCount !== 1 ? 's' : ''}
          </span>
          <span className="cmp-aud-sub">Score 80-100 (High Fit + Intent Signals)</span>
        </div>
        <div className="cmp-audience-stat-box med">
          <span className="cmp-aud-label">
            <i className="fas fa-check-circle" /> Medium Priority Tier
          </span>
          <span className="cmp-aud-value">
            {medPriorityCount} lead{medPriorityCount !== 1 ? 's' : ''}
          </span>
          <span className="cmp-aud-sub">Score 60-79 (Strong ICP Match)</span>
        </div>
        <div className="cmp-audience-stat-box low">
          <span className="cmp-aud-label">
            <i className="fas fa-layer-group" /> Low Priority Tier
          </span>
          <span className="cmp-aud-value">
            {lowPriorityCount} lead{lowPriorityCount !== 1 ? 's' : ''}
          </span>
          <span className="cmp-aud-sub">Score 40-59 (Moderate ICP Match)</span>
        </div>
      </div>

      <div className="cmp-eligibility-notice-box">
        <i className="fas fa-shield-alt" />
        <div>
          <strong>Automated Eligibility & Channel Verification Checks:</strong>
          <ul>
            <li>
              {formData.exclusionsCount || Math.round(perMatch * 0.15)} records excluded (
              <em>
                existing customers, previous unsubscribes, and active cross-campaign collisions
              </em>
              ).
            </li>
            <li>
              100% of selected prospects have verified work emails or LinkedIn URLs for chosen
              channels.
            </li>
          </ul>
        </div>
      </div>

      {/* ── AUTO-GENERATED LUSHA SUGGESTED LEADS (720 MATCHED CANDIDATES) ── */}
      {/* ── AUTO-GENERATED LUSHA SUGGESTED LEADS DISCOVERY POOL ── */}
      <div className="cmp-lusha-prospecting-container">
        {/* Top Header Card */}
        <div className="cmp-lusha-prospecting-header">
          <div className="cmp-lusha-prospecting-title-group">
            <div className="cmp-lusha-header-icon-badge">
              <i className="fas fa-bolt" />
            </div>
            <div>
              <div className="cmp-lusha-title-row">
                <h3 className="cmp-lusha-main-heading">AI & Lusha Suggested Leads</h3>
                <span className="cmp-lusha-count-badge">
                  <i className="fas fa-users" /> {lushaDisplayTotal.toLocaleString()} Matched
                  Candidates
                </span>
                <span className="cmp-lusha-engine-tag">
                  <i className="fas fa-shield-alt" /> Lusha Discovery Engine
                </span>
              </div>
              <div className="cmp-lusha-criteria-tags">
                <span className="cmp-criteria-pill">
                  <i className="fas fa-map-marker-alt" /> {targetRegion}
                </span>
                <span className="cmp-criteria-pill">
                  <i className="fas fa-building" /> {industrySector}
                </span>
                {targetPersonas.slice(0, 3).map((p, idx) => (
                  <span key={idx} className="cmp-criteria-pill cmp-criteria-pill--persona">
                    <i className="fas fa-user-tie" /> {p.replace(/\s*\([^)]*\)/g, '').trim()}
                  </span>
                ))}
                {targetPersonas.length > 3 && (
                  <span className="cmp-criteria-pill cmp-criteria-pill--more">
                    +{targetPersonas.length - 3} more
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="cmp-lusha-refresh-btn"
            onClick={fetchLushaSuggestions}
            disabled={loadingSuggestions || isInteractionDisabled}
            title={
              !hasQueriedLusha
                ? 'Generate candidate leads from Lusha'
                : 'Fetch new candidate suggestions from Lusha'
            }
          >
            <i
              className={`fas ${!hasQueriedLusha ? 'fa-bolt' : 'fa-sync-alt'} ${loadingSuggestions ? 'fa-spin' : ''}`}
            />
            <span>
              {loadingSuggestions
                ? 'Discovering…'
                : !hasQueriedLusha
                  ? 'Generate Leads'
                  : 'Refresh Pool'}
            </span>
          </button>
        </div>

        {suggestionFeedback && (
          <div className="cmp-lusha-feedback-banner">
            <i className="fas fa-check-circle" /> {suggestionFeedback}
          </div>
        )}

        {!hasQueriedLusha ? (
          <div className="cmp-lusha-inline-initial">
            <div className="cmp-lusha-initial-icon-wrap">
              <i className="fas fa-magic" />
            </div>
            <h4>Discover Targeted Decision-Makers with Lusha</h4>
            <p>
              Query the Lusha B2B Prospecting Engine on-demand to fetch candidate leads matching
              your Step 1 targeting criteria (<strong>{targetRegion}</strong> •{' '}
              <strong>{industrySector}</strong> •{' '}
              <strong>
                {targetPersonas
                  .slice(0, 3)
                  .map((p) => p.replace(/\s*\([^)]*\)/g, '').trim())
                  .join(', ')}
              </strong>
              {targetPersonas.length > 3 ? ` +${targetPersonas.length - 3} more` : ''}
              ).
            </p>
            <button
              type="button"
              className="cmp-lusha-generate-btn"
              onClick={fetchLushaSuggestions}
              disabled={loadingSuggestions || isInteractionDisabled}
            >
              <i className="fas fa-bolt" /> Generate Matched Leads from Lusha
            </button>
          </div>
        ) : loadingSuggestions ? (
          <div className="cmp-lusha-inline-loading">
            <div className="cmp-lusha-loader-spinner">
              <i className="fas fa-circle-notch fa-spin" />
            </div>
            <h4>Querying Lusha B2B Prospecting Engine…</h4>
            <p>
              Scanning verified decision-maker records matching your target region, industry sector,
              and job personas.
            </p>
          </div>
        ) : suggestedContacts.length === 0 ? (
          <div className="cmp-lusha-inline-empty">
            <div className="cmp-lusha-empty-icon-wrap">
              <i className="fas fa-search-minus" />
            </div>
            <h4>No Lead Profiles Matched</h4>
            <p>
              No candidate leads were found from Lusha matching your current ICP criteria (
              {targetRegion} • {industrySector} • {targetPersonas.join(', ')}).
            </p>
            <p className="cmp-empty-subtext">
              Try adjusting your target region, industry sector, or personas in Step 1, or import
              existing leads from your CRM Lead Pipeline.
            </p>
            <button
              type="button"
              className="cmp-lusha-retry-btn"
              onClick={fetchLushaSuggestions}
              disabled={loadingSuggestions || isInteractionDisabled}
            >
              <i className="fas fa-redo" /> Retry Lusha Search
            </button>
          </div>
        ) : (
          <div className="cmp-lusha-card-wrapper">
            {/* Action Bar */}
            <div className="cmp-lusha-action-bar">
              <div className="cmp-lusha-action-left">
                <div className="cmp-lusha-select-actions">
                  <button
                    type="button"
                    className="cmp-action-chip-btn"
                    onClick={handleToggleSelectCurrentPage}
                    disabled={isInteractionDisabled}
                  >
                    <i className="fas fa-check-square" /> Select Page ({paginatedCandidates.length})
                  </button>
                  <button
                    type="button"
                    className={`cmp-action-chip-btn cmp-action-chip-btn--accent ${selectedSuggestedIds.size === suggestedContacts.length ? 'active' : ''}`}
                    onClick={handleSelectAllMatchedPool}
                    disabled={isInteractionDisabled}
                  >
                    {selectedSuggestedIds.size === suggestedContacts.length ? (
                      <>
                        <i className="fas fa-times-circle" /> Deselect All
                      </>
                    ) : (
                      <>
                        <i className="fas fa-layer-group" /> Select All (
                        {suggestedContacts.length.toLocaleString()})
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="cmp-lusha-action-right">
                <div className="cmp-lusha-search-box">
                  <i className="fas fa-search cmp-lusha-inline-search-icon" />
                  <input
                    type="text"
                    placeholder="Search candidate, role or company…"
                    value={candidateSearch}
                    onChange={(e) => {
                      setCandidateSearch(e.target.value);
                      setCandidatePage(1);
                    }}
                    disabled={isInteractionDisabled}
                  />
                  {candidateSearch && (
                    <button
                      type="button"
                      className="cmp-search-clear-btn"
                      onClick={() => setCandidateSearch('')}
                      disabled={isInteractionDisabled}
                    >
                      <i className="fas fa-times" />
                    </button>
                  )}
                </div>

                <div className="cmp-reveal-selector-container">
                  <span className="cmp-reveal-title">Reveal:</span>
                  <div className="cmp-reveal-pill-slider">
                    {[
                      { value: 'both', icon: 'fa-bolt', label: 'All Data', cost: 6 },
                      { value: 'email', icon: 'fa-envelope', label: 'Email', cost: 1 },
                      { value: 'phone', icon: 'fa-phone', label: 'Phone', cost: 5 },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`cmp-reveal-slider-btn ${enrichType === opt.value ? 'active' : ''}`}
                        onClick={() => setEnrichType(opt.value)}
                        disabled={isInteractionDisabled}
                      >
                        <i className={`fas ${opt.icon}`} />
                        <span>{opt.label}</span>
                        <span className="cmp-reveal-cost-badge">{opt.cost} cr</span>
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  className="cmp-lusha-enrich-cta-btn cmp-btn-lusha-enrich-inline"
                  disabled={selectedSuggestedIds.size === 0 || isInteractionDisabled}
                  onClick={handleEnrichSelectedCandidates}
                >
                  {isEnriching ? (
                    <>
                      <i className="fas fa-circle-notch fa-spin" /> Enriching & Syncing…
                    </>
                  ) : (
                    <>
                      <i className="fas fa-coins" />
                      <span>
                        {selectedSuggestedIds.size > 0
                          ? `Enrich ${selectedSuggestedIds.size.toLocaleString()} Leads (${totalCreditsNeeded.toLocaleString()} cr)`
                          : 'Enrich Selected'}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Table Area */}
            <div className="cmp-lusha-table-responsive-box">
              <table className="cmp-lusha-table-v2">
                <thead>
                  <tr>
                    <th className="cmp-th-checkbox">
                      <input
                        type="checkbox"
                        checked={
                          paginatedCandidates.length > 0 &&
                          paginatedCandidates.every((c) => selectedSuggestedIds.has(c.id))
                        }
                        disabled={isInteractionDisabled}
                        onChange={handleToggleSelectCurrentPage}
                        title="Select all visible candidates"
                      />
                    </th>
                    <th className="cmp-th-prospect">PROSPECT</th>
                    <th className="cmp-th-role">JOB ROLE</th>
                    <th className="cmp-th-company">TARGET COMPANY</th>
                    <th className="cmp-th-location">LOCATION</th>
                    <th className="cmp-th-channels">AVAILABLE DATA</th>
                    <th className="cmp-th-cost">CREDITS</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCandidates.map((contact) => {
                    const isSelected = selectedSuggestedIds.has(contact.id);
                    let cost = 0;
                    if (enrichType === 'both') {
                      if (contact.hasEmail) cost += 1;
                      if (contact.hasPhone) cost += 5;
                    } else if (enrichType === 'email') {
                      if (contact.hasEmail) cost += 1;
                    } else if (enrichType === 'phone') {
                      if (contact.hasPhone) cost += 5;
                    }

                    const initials = (contact.name || 'L')
                      .split(' ')
                      .filter(Boolean)
                      .map((n) => n[0])
                      .slice(0, 2)
                      .join('')
                      .toUpperCase();

                    return (
                      <tr
                        key={contact.id}
                        className={`cmp-lusha-prospect-row ${isSelected ? 'is-selected' : ''}`}
                        onClick={() =>
                          !isInteractionDisabled && handleToggleSelectCandidate(contact.id)
                        }
                      >
                        <td className="cmp-td-checkbox" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isInteractionDisabled}
                            onChange={() => handleToggleSelectCandidate(contact.id)}
                          />
                        </td>
                        <td className="cmp-td-prospect">
                          <div className="cmp-prospect-profile-cell">
                            <div className="cmp-prospect-avatar">{initials}</div>
                            <div className="cmp-prospect-info">
                              <span className="cmp-prospect-fullname">{contact.name}</span>
                              <span className="cmp-prospect-badge">
                                <i className="fas fa-bolt" /> Lusha Verified
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="cmp-td-role">
                          <span className="cmp-role-badge-text" title={contact.jobTitle}>
                            {contact.jobTitle}
                          </span>
                        </td>
                        <td className="cmp-td-company">
                          <div className="cmp-company-cell-content">
                            <span className="cmp-company-name-text">
                              <i className="fas fa-building cmp-company-icon" /> {contact.company}
                            </span>
                            <span className="cmp-company-sector-pill">
                              {contact.industry || industrySector}
                            </span>
                          </div>
                        </td>
                        <td className="cmp-td-location">
                          <span className="cmp-location-badge-pill">
                            <i className="fas fa-map-marker-alt" /> {contact.location}
                          </span>
                        </td>
                        <td className="cmp-td-channels">
                          <div className="cmp-channels-badge-group">
                            {contact.hasEmail && (
                              <span
                                className="cmp-channel-pill cmp-channel-pill--email"
                                title="Verified Work Email Available"
                              >
                                <i className="fas fa-envelope" /> Email
                              </span>
                            )}
                            {contact.hasPhone && (
                              <span
                                className="cmp-channel-pill cmp-channel-pill--phone"
                                title="Direct Phone Number Available"
                              >
                                <i className="fas fa-phone" /> Phone
                              </span>
                            )}
                            {contact.linkedinUrl && (
                              <span
                                className="cmp-channel-pill cmp-channel-pill--linkedin"
                                title="LinkedIn Profile Available"
                              >
                                <i className="fab fa-linkedin-in" />
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="cmp-td-cost">
                          <span className="cmp-cost-token-badge">
                            <i className="fas fa-coins" /> {cost} cr
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            {totalCandidatePages > 1 && (
              <div className="cmp-lusha-footer-pagination">
                <div className="cmp-footer-count-text">
                  Showing{' '}
                  <strong>{((candidatePage - 1) * candidatePageSize + 1).toLocaleString()}</strong>–
                  <strong>
                    {Math.min(
                      candidatePage * candidatePageSize,
                      filteredCandidates.length
                    ).toLocaleString()}
                  </strong>{' '}
                  of <strong>{filteredCandidates.length.toLocaleString()}</strong> candidates
                </div>
                <div className="cmp-footer-pager-controls">
                  <button
                    type="button"
                    className="cmp-pager-nav-btn"
                    disabled={candidatePage === 1 || isInteractionDisabled}
                    onClick={() => setCandidatePage((p) => Math.max(1, p - 1))}
                    title="Previous page"
                  >
                    <i className="fas fa-chevron-left" /> Previous
                  </button>

                  <div className="cmp-pager-numbers-wrap">
                    {Array.from({ length: Math.min(7, totalCandidatePages) }, (_, i) => {
                      let pg;
                      if (totalCandidatePages <= 7) {
                        pg = i + 1;
                      } else if (candidatePage <= 4) {
                        pg = i < 5 ? i + 1 : i === 5 ? '…' : totalCandidatePages;
                      } else if (candidatePage >= totalCandidatePages - 3) {
                        pg = i === 0 ? 1 : i === 1 ? '…' : totalCandidatePages - (6 - i);
                      } else {
                        const map = [
                          1,
                          '…',
                          candidatePage - 1,
                          candidatePage,
                          candidatePage + 1,
                          '…',
                          totalCandidatePages,
                        ];
                        pg = map[i];
                      }
                      return pg === '…' ? (
                        <span key={`el-${i}`} className="cmp-pager-ellipsis-text">
                          …
                        </span>
                      ) : (
                        <button
                          key={pg}
                          type="button"
                          className={`cmp-pager-num-btn ${candidatePage === pg ? 'is-active' : ''}`}
                          disabled={isInteractionDisabled}
                          onClick={() => setCandidatePage(pg)}
                        >
                          {pg}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    className="cmp-pager-nav-btn"
                    disabled={candidatePage === totalCandidatePages || isInteractionDisabled}
                    onClick={() => setCandidatePage((p) => Math.min(totalCandidatePages, p + 1))}
                    title="Next page"
                  >
                    Next <i className="fas fa-chevron-right" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── ENRICHED & IMPORTED CAMPAIGN TARGET COHORT TABLE ── */}
      <div className="cmp-imported-leads-section">
        <div className="cmp-imported-leads-header">
          <div className="cmp-imported-header-left">
            <h3 className="cmp-imported-title">
              <i className="fas fa-users" /> Active Campaign Target Cohort ({totalCohortLeadsCount})
            </h3>
            <p className="cmp-imported-sub">
              Verified decision-makers attached to this campaign cohort from Lusha enrichment and
              your CRM Lead Pipeline.
            </p>
          </div>
          {totalCohortLeadsCount > 0 && (
            <div className="cmp-imported-header-actions">
              <button
                type="button"
                className="cmp-btn-primary cmp-import-pipeline-btn"
                disabled={isInteractionDisabled}
                onClick={onOpenImportModal}
              >
                <i className="fas fa-plus" /> Import from Pipeline
              </button>
            </div>
          )}
        </div>

        {totalCohortLeadsCount === 0 ? (
          <div className="cmp-imported-empty-card">
            <i className="fas fa-user-plus cmp-empty-icon" />
            <h4>No Leads Added to Campaign Cohort Yet</h4>
            <p>
              Select suggested candidates from the {suggestedContacts.length} matched leads above to
              enrich and add, or import existing leads directly from your CRM Lead Pipeline.
            </p>
            <button
              type="button"
              className="cmp-btn-primary"
              disabled={isInteractionDisabled}
              onClick={onOpenImportModal}
            >
              <i className="fas fa-file-import" /> Select Leads from Pipeline
            </button>
          </div>
        ) : (
          <div className="cmp-imported-table-card">
            <div className="cmp-imported-table-toolbar">
              <div className="cmp-imported-toolbar-left">
                <div className="cmp-imported-search-wrap">
                  <i className="fas fa-search" />
                  <input
                    type="text"
                    placeholder="Filter cohort leads by name, company, title, source..."
                    value={importedSearch}
                    disabled={isGeneratingAi}
                    onChange={(e) => setImportedSearch(e.target.value)}
                  />
                  {importedSearch && (
                    <button
                      type="button"
                      className="cmp-search-clear"
                      disabled={isGeneratingAi}
                      onClick={() => setImportedSearch('')}
                    >
                      <i className="fas fa-times" />
                    </button>
                  )}
                </div>
                {selectedImportedIds.size > 0 && (
                  <button
                    type="button"
                    className="cmp-btn-delete-selected"
                    disabled={isInteractionDisabled}
                    onClick={onDeleteSelectedImported}
                    title={`Delete ${selectedImportedIds.size} selected lead${selectedImportedIds.size > 1 ? 's' : ''}`}
                  >
                    <i className="fas fa-trash-alt" />
                    <span>Delete ({selectedImportedIds.size})</span>
                  </button>
                )}
              </div>
              <div className="cmp-imported-summary-pills">
                <span className="cmp-summary-pill high">
                  ★ {normalizedCohortLeads.filter((l) => l.leadScore >= 80).length} High Fit
                  (80-100)
                </span>
                <span className="cmp-summary-pill med">
                  ●{' '}
                  {
                    normalizedCohortLeads.filter((l) => l.leadScore >= 60 && l.leadScore < 80)
                      .length
                  }{' '}
                  Med Fit (60-79)
                </span>
                <span className="cmp-summary-pill low">
                  ● {normalizedCohortLeads.filter((l) => l.leadScore < 60).length} Low Fit (&lt;60)
                </span>
              </div>
            </div>

            <div className="cmp-imported-table-responsive">
              <table className="cmp-imported-table">
                <thead>
                  <tr>
                    <th className="cmp-col-select">
                      <input
                        type="checkbox"
                        checked={isAllImportedSelected}
                        disabled={isInteractionDisabled}
                        onChange={onToggleSelectAllImported}
                        title="Select all"
                        className="cmp-table-checkbox"
                      />
                    </th>
                    <th>Lead Contact</th>
                    <th>Source</th>
                    <th>Title & Role</th>
                    <th>Company & Sector</th>
                    <th>Location</th>
                    <th>Pipeline Stage</th>
                    <th>ICP Fit Score</th>
                    <th>Channels</th>
                    {!isReadOnly && <th className="text-center">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredCohortLeads.map((lead) => {
                    const isSelected = selectedImportedIds.has(lead.id);
                    const isLushaSource =
                      lead.provider === 'lusha' ||
                      (typeof lead.source === 'string' &&
                        lead.source.toLowerCase().includes('lusha'));

                    return (
                      <tr key={lead.id} className={isSelected ? 'cmp-row-selected' : ''}>
                        <td className="cmp-col-select">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isInteractionDisabled}
                            onChange={() => onToggleSelectImported(lead.id)}
                            className="cmp-table-checkbox"
                          />
                        </td>
                        <td>
                          <div className="cmp-lead-contact-cell">
                            <div
                              className={`cmp-lead-avatar ${isLushaSource ? 'cmp-avatar-lusha' : ''}`}
                            >
                              {lead.name
                                ? lead.name
                                    .split(' ')
                                    .map((n) => n[0])
                                    .slice(0, 2)
                                    .join('')
                                    .toUpperCase()
                                : 'L'}
                            </div>
                            <div>
                              <strong className="cmp-lead-name">{lead.name}</strong>
                              <span className="cmp-lead-email">
                                {lead.email || 'No email specified'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          {isLushaSource ? (
                            <span
                              className="cmp-source-tag lusha"
                              title="Enriched via Lusha API & Saved to Pipeline"
                            >
                              <i className="fas fa-magic" /> Lusha Enriched
                            </span>
                          ) : (
                            <span
                              className="cmp-source-tag pipeline"
                              title="Imported from CRM Lead Pipeline"
                            >
                              <i className="fas fa-database" /> CRM Pipeline
                            </span>
                          )}
                        </td>
                        <td>
                          <span className="cmp-lead-title">{lead.jobTitle}</span>
                        </td>
                        <td>
                          <strong className="cmp-lead-company">{lead.company}</strong>
                          <span className="cmp-lead-industry-tag">{lead.industry}</span>
                        </td>
                        <td>
                          <span className="cmp-lead-country">
                            <i className="fas fa-map-marker-alt" /> {lead.country}
                          </span>
                        </td>
                        <td>
                          <span className={`cmp-stage-badge ${getStageClass(lead.stage)}`}>
                            {lead.stage}
                          </span>
                        </td>
                        <td>
                          <span className={`cmp-score-badge ${getScoreClass(lead.leadScore)}`}>
                            {lead.leadScore} Fit
                          </span>
                        </td>
                        <td>
                          <div className="cmp-lead-channels-icons">
                            {lead.email && (
                              <i className="fas fa-envelope active" title={lead.email} />
                            )}
                            {lead.linkedinUrl && (
                              <i className="fab fa-linkedin active" title="LinkedIn Profile" />
                            )}
                            {lead.phone && <i className="fas fa-phone active" title={lead.phone} />}
                          </div>
                        </td>
                        {!isReadOnly && (
                          <td className="text-center">
                            <button
                              type="button"
                              className="cmp-btn-remove-lead"
                              disabled={isInteractionDisabled}
                              title="Remove lead from campaign cohort"
                              onClick={() => onRemoveImportedLead(lead.id)}
                            >
                              <i className="fas fa-trash-alt" />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
